import { useCallback, useEffect, useState } from 'react';
import usePreciseTimer from '@hooks/usePreciseTimer';
import { zeroPad } from '@cont/Quiz/utils';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import {
  getCurrentSession,
  getOngoingSectionDetails
} from '@selectors/quizTaking';
import useFinishQuiz from '../../Taking/Common/useFinishQuiz';
import useFailedAPIQueue from '../../Taking/Common/useFailedAPIQueue';

const formatRemainingTime = (totalSeconds) => {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  return `${zeroPad(hours)}:${zeroPad(minutes)}:${zeroPad(seconds)}`;
};

const useNtaTimer = (enabled = true, onExpire) => {
  const { finishQuiz } = useFinishQuiz();
  const { forceProcessAPISImmediately } = useFailedAPIQueue();
  const currentSession = useSelectorWithShallowEqual(getCurrentSession);
  const ongoingSectionDetails = useSelectorWithShallowEqual(
    getOngoingSectionDetails
  );

  const hasSectionalTimer = !!ongoingSectionDetails.section;

  const getRemainingTime = useCallback(
    () =>
      hasSectionalTimer
        ? ongoingSectionDetails.time_remaining
        : currentSession.time_remaining,
    [
      currentSession.time_remaining,
      hasSectionalTimer,
      ongoingSectionDetails.time_remaining
    ]
  );

  const [expiryTime, setExpiryTime] = useState(getRemainingTime);
  const [remainingLabel, setRemainingLabel] = useState(() =>
    formatRemainingTime(getRemainingTime())
  );
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const next = getRemainingTime();
    setExpiryTime(next);
    setRemainingLabel(formatRemainingTime(next));
    setExpired(false);
  }, [getRemainingTime]);

  const tick = useCallback(
    (elapsedSeconds) => {
      if (expired) return;

      if (expiryTime - elapsedSeconds <= 10) {
        forceProcessAPISImmediately();
      }

      const distance = expiryTime - elapsedSeconds;

      if (distance < 0) {
        setRemainingLabel('00:00:00');
        setExpired(true);
        if (onExpire) {
          onExpire();
        } else {
          finishQuiz();
        }
        return;
      }

      setRemainingLabel(formatRemainingTime(distance));
      setExpiryTime(distance);
    },
    [expired, expiryTime, finishQuiz, forceProcessAPISImmediately, onExpire]
  );

  usePreciseTimer(tick, 1000, enabled && !expired);

  return { remainingTime: enabled ? remainingLabel : undefined };
};

export default useNtaTimer;
